import type { AdminUser } from "./AdminUserCard";

/** Determine how far through the funnel a user has progressed (0-3). */
function getFunnelStep(user: AdminUser): number {
  const isEngaged =
    user.usage.vehicles > 0 ||
    user.usage.expenses > 0 ||
    user.usage.trips > 0 ||
    user.usage.maintenance > 0 ||
    user.usage.ai_chats > 0;
  if (isEngaged) return 3;
  if (user.last_sign_in_at) return 2;
  if (user.email_confirmed_at) return 1;
  return 0;
}

const steps = [
  { label: "Signed Up", activeClass: "bg-muted-foreground text-white" },
  { label: "Confirmed", activeClass: "bg-blue-500 text-white" },
  { label: "First Access", activeClass: "bg-amber-500 text-white" },
  { label: "Engaged", activeClass: "" }, // special glow handled below
];

export default function UserFunnelTimeline({ user }: { user: AdminUser }) {
  const current = getFunnelStep(user);

  return (
    <div className="flex items-center w-full">
      {steps.map((step, i) => {
        const isCurrent = i === current;
        const isEngaged = i === 3 && isCurrent;

        return (
          <div key={step.label} className="flex items-center flex-1 min-w-0 last:flex-none">
            {/* Step pill */}
            <span
              className={`
                inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap leading-tight
                ${
                  isEngaged
                    ? "text-white"
                    : isCurrent
                    ? step.activeClass
                    : "bg-muted text-muted-foreground/50"
                }
              `}
              style={
                isEngaged
                  ? {
                      background:
                        "linear-gradient(135deg, #059669 0%, #34d399 40%, #6ee7b7 50%, #34d399 60%, #059669 100%)",
                      backgroundSize: "200% 200%",
                      animation: "ribbon-shine 3s ease-in-out infinite",
                      boxShadow: "0 2px 6px rgba(5, 150, 105, 0.4)",
                    }
                  : undefined
              }
            >
              {step.label}
            </span>

            {/* Arrow connector centered in remaining space */}
            {i < steps.length - 1 && (
              <div className="flex-1 flex items-center justify-center">
                <svg
                  className="shrink-0"
                  width="12"
                  height="10"
                  viewBox="0 0 12 10"
                  fill="none"
                >
                  <path
                    d="M0 5 H8 M6 2 L9 5 L6 8"
                    stroke="hsl(var(--muted-foreground) / 0.25)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            )}
          </div>
        );
      })}

      <style>{`
        @keyframes ribbon-shine {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </div>
  );
}
